import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { AudioWaveform } from '@/components/AudioWaveform';
import { ResultView } from '@/components/fanbase-fix/ResultView';
import { ArrowLeft } from 'lucide-react';
import { steps, personalisationFields, type QuestionKey } from '@/features/fanbase-fix/questions';
import { emptyAnswers, resolveAnswers, type Answers } from '@/features/fanbase-fix/types';
import { selectPlaybooks } from '@/features/fanbase-fix/selector';
import { playbooks } from '@/features/fanbase-fix/playbooks';
import {
  FANBASE_FIX_CANONICAL,
  FANBASE_FIX_NOINDEX,
  PRIVACY_URL,
} from '@/features/fanbase-fix/config';

const PAGE_TITLE = 'The Fanbase Fix | DK Artists';
const PAGE_DESCRIPTION =
  'Answer a few questions about how you are promoting your music and leave with one thing to fix first, prepared for you.';

type Screen = 'landing' | 'quiz' | 'personalise' | 'contact' | 'result';

const primaryButton =
  'inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-white px-8 py-4 font-semibold text-[#1d1d1f] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(255,255,255,0.15)] disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:translate-y-0 disabled:hover:shadow-none sm:w-auto';

export default function FanbaseFix() {
  const [screen, setScreen] = useState<Screen>('landing');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>(emptyAnswers());
  const [contact, setContact] = useState({ firstName: '', email: '' });
  const [contactError, setContactError] = useState('');

  const step = steps[stepIndex];

  // Provisional selection decides which three personalisation fields to ask for.
  const provisional = useMemo(() => selectPlaybooks(answers), [answers]);
  const fields = personalisationFields[playbooks[provisional.primary].category];

  // Final selection is re-run after the personalisation answers are confirmed.
  const finalSelection = useMemo(() => selectPlaybooks(answers), [answers]);
  const result = useMemo(
    () => playbooks[finalSelection.primary].build(resolveAnswers(answers)),
    [finalSelection.primary, answers],
  );

  const toggle = (key: QuestionKey, id: string, exclusives: string[]) => {
    setAnswers((prev) => {
      const current = prev[key] as string[];
      let next: string[];
      if (current.includes(id)) next = current.filter((v) => v !== id);
      else if (exclusives.includes(id)) next = [id];
      else next = [...current.filter((v) => !exclusives.includes(v)), id];
      return { ...prev, [key]: next } as Answers;
    });
  };

  const stepComplete = (() => {
    if (!step) return false;
    if (step.kind === 'text') return true;
    return (answers[step.id as QuestionKey] as string[]).length > 0;
  })();

  const personalisationComplete = fields
    .filter((f) => !f.optional)
    .every((f) => (answers.personalisation[f.id] || '').trim().length > 0);

  const goNext = () => {
    if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
    else setScreen('personalise');
  };

  const goBack = () => {
    if (screen === 'contact') return setScreen('personalise');
    if (screen === 'personalise') {
      setStepIndex(steps.length - 1);
      return setScreen('quiz');
    }
    if (stepIndex === 0) return setScreen('landing');
    setStepIndex(stepIndex - 1);
  };

  const submitContact = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact.firstName.trim()) return setContactError('Please add your first name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim()))
      return setContactError('Please enter a valid email address.');
    setContactError('');
    // Stage 1: nothing is stored and nothing is sent. The plan is simply revealed.
    setScreen('result');
  };

  const phase = screen === 'result' ? 'Your plan' : screen === 'landing' ? 'Your situation' : step?.phase ?? 'Your plan';

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        {FANBASE_FIX_NOINDEX && <meta name="robots" content="noindex,nofollow" />}
        <link rel="canonical" href={FANBASE_FIX_CANONICAL} />
      </Helmet>

      <Navigation />

      <DarkChapter>
        <main className="min-h-screen pt-28 md:pt-36">
          {screen === 'landing' && (
            <section className="mx-auto max-w-3xl px-6 pb-24 text-center lg:px-0">
              <div className="mb-6 flex items-center justify-center gap-3">
                <AudioWaveform />
                <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1 lg:px-2.5 lg:py-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50 lg:text-xs">
                    &gt; FREE ARTIST TOOL
                  </span>
                </div>
              </div>

              <h1 className="font-headline text-[clamp(2rem,6.5vw,3.75rem)] font-bold leading-[1.05] text-white">
                WHAT SHOULD YOU FIX FIRST TO GROW YOUR AUDIENCE?
              </h1>

              <div className="mx-auto mt-7 max-w-xl space-y-4 text-left text-[15px] leading-relaxed text-white/70">
                <p>You could spend another week trying a bit of everything.</p>
                <p>
                  Answer a few questions about how you&apos;re promoting your music and the response you&apos;re
                  getting. The Fanbase Fix will help you choose where to focus next, then prepare something
                  practical for you to use.
                </p>
                <p>
                  It might be a better way to introduce your song. It might be a clearer route to listening. You
                  won&apos;t be given the same plan as everyone else.
                </p>
              </div>

              <button type="button" className={`${primaryButton} mt-8`} onClick={() => setScreen('quiz')}>
                FIND MY FIRST FIX
              </button>

              <p className="mx-auto mt-5 max-w-xl text-sm text-white/45">
                Free. No account needed. We&apos;ll ask for your email to show your plan and send you a copy.
              </p>
              <p className="mx-auto mt-3 max-w-xl text-xs text-white/35">
                Built around DK&apos;s strategy framework. No automatic audio or account analysis.
              </p>
            </section>
          )}

          {screen !== 'landing' && screen !== 'result' && (
            <section className="mx-auto max-w-2xl px-6 pb-24 lg:px-0">
              <div className="mb-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={goBack}
                  className="inline-flex items-center gap-1.5 text-sm text-white/50 transition-colors hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden /> Back
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
                  {screen === 'quiz' ? `${phase} · ${stepIndex + 1} of ${steps.length}` : phase}
                </span>
              </div>

              {screen === 'quiz' && step && (
                <div>
                  <h2 className="font-headline text-2xl font-bold leading-tight text-white md:text-3xl">
                    {step.heading}
                  </h2>
                  {step.helper && <p className="mt-2 text-sm text-white/50">{step.helper}</p>}

                  {step.kind === 'text' ? (
                    <div className="mt-7">
                      <label htmlFor="songTitle" className="mb-2 block text-sm font-semibold text-white/80">
                        Song or project title
                      </label>
                      <input
                        id="songTitle"
                        value={answers.songTitle}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, songTitle: e.target.value }))}
                        className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#0071e3] focus:outline-none"
                        placeholder="Leave blank if you'd rather not"
                      />
                    </div>
                  ) : (
                    <div className="mt-7 space-y-2">
                      {step.options?.map((o) => (
                        <OptionButton
                          key={o.id}
                          label={o.label}
                          selected={(answers[step.id as QuestionKey] as string[]).includes(o.id)}
                          onClick={() => toggle(step.id as QuestionKey, o.id, step.exclusiveOptions ?? [])}
                        />
                      ))}
                    </div>
                  )}

                  <button type="button" className={`${primaryButton} mt-8`} disabled={!stepComplete} onClick={goNext}>
                    Continue
                  </button>
                </div>
              )}

              {screen === 'personalise' && (
                <div>
                  <h2 className="font-headline text-2xl font-bold leading-tight text-white md:text-3xl">
                    Now let&apos;s make this useful for your music.
                  </h2>
                  <p className="mt-2 text-sm text-white/55">
                    A couple of details will help us turn your result into something you can actually use. Short
                    answers are fine.
                  </p>
                  <p className="mt-2 text-xs text-white/35">
                    Please only share lyrics or material you have the right to share, and keep other people&apos;s
                    details out of it.
                  </p>

                  <div className="mt-7 space-y-6">
                    {fields.map((f) => (
                      <div key={f.id}>
                        <label htmlFor={f.id} className="mb-2 block text-sm font-semibold text-white/85">
                          {f.label}
                          {f.optional && <span className="ml-1 font-normal text-white/40">(optional)</span>}
                        </label>
                        {f.helper && <p className="mb-2 text-xs text-white/40">{f.helper}</p>}
                        {f.kind === 'select' ? (
                          <div className="space-y-2">
                            {f.options?.map((o) => (
                              <OptionButton
                                key={o.id}
                                label={o.label}
                                selected={answers.personalisation[f.id] === o.id}
                                onClick={() =>
                                  setAnswers((prev) => ({
                                    ...prev,
                                    personalisation: { ...prev.personalisation, [f.id]: o.id },
                                  }))
                                }
                              />
                            ))}
                          </div>
                        ) : (
                          <textarea
                            id={f.id}
                            rows={3}
                            value={answers.personalisation[f.id] ?? ''}
                            onChange={(e) =>
                              setAnswers((prev) => ({
                                ...prev,
                                personalisation: { ...prev.personalisation, [f.id]: e.target.value },
                              }))
                            }
                            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#0071e3] focus:outline-none"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className={`${primaryButton} mt-8`}
                    disabled={!personalisationComplete}
                    onClick={() => setScreen('contact')}
                  >
                    Continue
                  </button>
                  <p className="mt-3 text-xs text-white/35">
                    If you skip detail we will not invent it. You will get a clearly labelled starting version.
                  </p>
                </div>
              )}

              {screen === 'contact' && (
                <form onSubmit={submitContact}>
                  <h2 className="font-headline text-2xl font-bold leading-tight text-white md:text-3xl">
                    Where should we send your fix plan?
                  </h2>
                  <p className="mt-2 text-sm text-white/55">
                    Enter your name and email to see your personalised result and receive a copy.
                  </p>

                  <div className="mt-7 space-y-4">
                    <Field
                      id="firstName"
                      label="First name"
                      value={contact.firstName}
                      onChange={(v) => setContact({ ...contact, firstName: v })}
                    />
                    <Field
                      id="email"
                      label="Email"
                      type="email"
                      value={contact.email}
                      onChange={(v) => setContact({ ...contact, email: v })}
                    />
                  </div>

                  {contactError && <p className="mt-4 text-sm text-red-400">{contactError}</p>}

                  <button type="submit" className={`${primaryButton} mt-7`}>
                    SHOW MY FIX PLAN
                  </button>

                  <p className="mt-4 text-xs leading-relaxed text-white/40">
                    Your email is used to deliver this plan.{' '}
                    <a href={PRIVACY_URL} className="underline underline-offset-2 hover:text-white/70">
                      Privacy policy
                    </a>
                    .
                  </p>
                  <p className="mt-2 text-xs text-white/30">
                    In this review build nothing is saved and no email is sent.
                  </p>
                </form>
              )}
            </section>
          )}

          {screen === 'result' && (
            <div className="pb-10 pt-4">
              <ResultView
                answers={answers}
                selection={finalSelection}
                result={result}
                onCorrect={() => {
                  setScreen('quiz');
                  setStepIndex(0);
                }}
              />
            </div>
          )}
        </main>
      </DarkChapter>

      <Footer />
    </>
  );
}

function OptionButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full rounded-xl border px-4 py-3.5 text-left text-[15px] leading-snug transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0071e3] ${
        selected
          ? 'border-[#0071e3] bg-[#0071e3]/15 text-white'
          : 'border-white/12 bg-white/[0.03] text-white/75 hover:border-white/25 hover:bg-white/[0.06]'
      }`}
    >
      {label}
    </button>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-semibold text-white/85">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#0071e3] focus:outline-none"
      />
    </div>
  );
}
